import {
  type LucideIcon,
  Accessibility, AirVent, BadgeCheck, BookOpen, Building2, CalendarDays, Car,
  Clock3, Compass, Droplets, FileText, GraduationCap, HandHeart, Heart,
  HeartHandshake, HeartPulse, Landmark, Library, MapPin, Megaphone, Moon, Navigation,
  Phone, Radio, ReceiptText, ShieldCheck, Sparkles, Star, Store, Sun, Sunrise,
  Trophy, Users, UtensilsCrossed, Wallet, WalletCards,
} from 'lucide-react'

/* PART 3 — site-wide icon map. One source of truth for label → lucide icon so
   every chip / tile / action that pairs an icon with a label resolves the same
   glyph across the app. Lookup is normalized (lowercase, punctuation-stripped)
   and matched on whole words, so "Women's section" and "Women section" both hit
   the same entry. Returns null when there is no confident match — callers fall
   back to their own default rather than rendering a wrong icon. */

const ICONS: Array<{ keys: string[]; icon: LucideIcon }> = [
  // facilities & access
  { keys: ['women', 'womens section', 'sisters'], icon: Users },
  { keys: ['wudu', 'ablution'], icon: Droplets },
  { keys: ['parking', 'car park'], icon: Car },
  { keys: ['wheelchair', 'accessible', 'ramp'], icon: Accessibility },
  { keys: ['ac', 'ac hall', 'air conditioned', 'cooling'], icon: AirVent },
  { keys: ['quran classes', 'quran', 'hifz', 'maktab'], icon: BookOpen },
  { keys: ['madrassa', 'madarsa', 'school', 'education'], icon: GraduationCap },
  { keys: ['library', 'kutubkhana'], icon: Library },
  { keys: ['nikah', 'nikah services', 'marriage'], icon: HeartHandshake },
  { keys: ['qibla', 'direction'], icon: Compass },
  // notices & content
  { keys: ['janaza', 'funeral'], icon: Moon },
  { keys: ['announcement', 'announcements', 'notice', 'notices'], icon: Megaphone },
  { keys: ['event', 'events', 'program', 'programme'], icon: CalendarDays },
  { keys: ['khutba', 'khutbah', 'lecture', 'bayan'], icon: BookOpen },
  { keys: ['prayer', 'prayer times', 'salah', 'namaz'], icon: Clock3 },
  { keys: ['sunrise'], icon: Sunrise },
  { keys: ['zawal', 'noon'], icon: Sun },
  { keys: ['hijri', 'date', 'calendar'], icon: CalendarDays },
  // giving & finance
  { keys: ['donate', 'donation', 'campaign', 'sadaqah', 'zakat'], icon: HandHeart },
  { keys: ['finance', 'ledger', 'expenses', 'wallet'], icon: Wallet },
  { keys: ['receipt', 'receipts', 'transparency'], icon: ReceiptText },
  { keys: ['subscription', 'plan', 'pricing'], icon: WalletCards },
  // trust & roles
  { keys: ['verified', 'verification', 'trust', 'registration', 'claim', 'claimed'], icon: BadgeCheck },
  { keys: ['protection', 'protected', 'security', 'safety'], icon: ShieldCheck },
  { keys: ['waqf', 'registry', 'linkage'], icon: Landmark },
  { keys: ['document', 'documents', 'evidence', 'records', 'source'], icon: FileText },
  { keys: ['health', 'score', 'health score'], icon: HeartPulse },
  { keys: ['capacity', 'occupancy'], icon: Users },
  { keys: ['volunteer', 'volunteers', 'khadim'], icon: Users },
  { keys: ['leaderboard', 'rank', 'award', 'achievement'], icon: Trophy },
  // places & contact
  { keys: ['location', 'address', 'area', 'locality', 'map'], icon: MapPin },
  { keys: ['directions', 'route', 'navigate'], icon: Navigation },
  { keys: ['phone', 'call', 'contact', 'mobile'], icon: Phone },
  { keys: ['masjid', 'mosque', 'institution'], icon: Building2 },
  { keys: ['business', 'businesses', 'directory', 'shop', 'store'], icon: Store },
  { keys: ['restaurant', 'halal', 'food', 'catering'], icon: UtensilsCrossed },
  { keys: ['live', 'realtime', 'feed'], icon: Radio },
  { keys: ['follow', 'favourite', 'favorite'], icon: Heart },
  { keys: ['featured', 'highlight', 'star'], icon: Star },
  { keys: ['ai', 'smart', 'intelligence'], icon: Sparkles },
]

function normalize(label: string): string {
  return String(label || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim()
}

/** Resolve a label to its icon, or null when there's no confident match. */
export function iconForLabel(label: string): LucideIcon | null {
  const n = normalize(label)
  if (!n) return null
  // exact phrase match first, then whole-word match
  for (const entry of ICONS) {
    if (entry.keys.some((k) => k === n)) return entry.icon
  }
  const words = new Set(n.split(' '))
  for (const entry of ICONS) {
    if (entry.keys.some((k) => k.split(' ').every((w) => words.has(w)))) return entry.icon
  }
  return null
}

/** Resolve to an icon with a guaranteed fallback (default Building2). */
export function iconForLabelOr(label: string, fallback: LucideIcon = Building2): LucideIcon {
  return iconForLabel(label) || fallback
}
